import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MediaTypeUtil } from '../../services/MediaTypeUtil';
import { FileMeta, FileState } from '../../services/files/FileMeta';
import { FileService } from '../../services/files/file.service';
import { DateUtil } from '../../DateUtil';
import { UserService } from '../../services/user/user.service';
import { FileUploadLogs } from '../../services/files/FileUploadLogs';

@Component({
  selector: 'digires-file-card',
  templateUrl: './file-card.component.html',
  styleUrls: ['./file-card.component.scss'],
})
export class FileCardComponent implements OnChanges {
  private readonly maxFileSize = 16777216;
  readonly fileState: typeof FileState = FileState;
  readonly placeholderFileState = {
    [FileState.UPLOADED]: 'Uploaded',
    [FileState.ARCHIVED]: 'Archived',
    [FileState.DELETED]: 'Deleted',
  };

  @ViewChild('downloadLink') link: HTMLAnchorElement;

  @Output() upload: EventEmitter<File> = new EventEmitter<File>();
  @Output() archive: EventEmitter<void> = new EventEmitter<void>();
  @Output() openArchive: EventEmitter<void> = new EventEmitter<void>();
  @Output() delete: EventEmitter<void> = new EventEmitter<void>();
  @Output() titleClicked: EventEmitter<void> = new EventEmitter<void>();

  @Input() title: string;
  @Input() fileMeta: FileMeta;
  @Input() fileUploadLogs: FileUploadLogs[];
  @Input() fileId: string;
  @Input() archivable: boolean = false;
  @Input() archiveOpenable: boolean = false;
  @Input() deletable: boolean = false;
  @Input() isAllowedToEditTitle: boolean = false;
  @Input() isAllowedToArchive: boolean = false;
  @Input() isAllowedToOpenArchive: boolean = false;
  @Input() isAllowedToDelete: boolean = false;
  @Input() isAllowedToUpload: boolean = false;
  @Input() acceptedFileTypes: string[] = MediaTypeUtil.EXCEL_TYPES;
  @Input() disableUpload: boolean = false;
  @Input() disableUploadTooltip: string = null;

  changeUserName: string;
  changeDateTime: string;
  usedFileMeta: FileMeta;

  selectedFile: File;
  selectedFileTypeInvalid: boolean;
  selectedFileSizeInvalid: boolean;

  constructor(
    private fileService: FileService,
    private userService: UserService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.fileMeta || changes.fileId) {
      if (this.fileMeta) {
        this.usedFileMeta = this.fileMeta;
        this.onFileMetaChanged();
      } else if (this.fileId) {
        this.usedFileMeta = null;
        this.onFileMetaChanged();
        this.fileService.loadFileMeta(this.fileId).then((fileMeta) => {
          this.usedFileMeta = fileMeta;
          this.onFileMetaChanged();
        });
      } else {
        this.usedFileMeta = null;
        this.onFileMetaChanged();
      }
    }
  }

  onFileMetaChanged() {
    if (this.usedFileMeta) {
      const userId =
        this.usedFileMeta.changeUserId || this.usedFileMeta.uploadUserId;
      this.changeUserName = userId;
      this.changeDateTime = DateUtil.ddmmyyyyHHMM_TZ(
        this.usedFileMeta.changeDateTime || this.usedFileMeta.uploadDateTime,
      );

      if (userId) {
        this.userService
          .loadUserById(userId)
          .then((user) => {
            this.changeUserName = user.fullName;
          })
          .catch((error) => {
            console.error('loadUserById.error=', error);
            this.changeUserName = userId;
          });
      }
    } else {
      this.changeUserName = null;
      this.changeDateTime = null;
    }
  }

  onFileForUploadSelected(event: Event) {
    if (event?.target?.['files']?.length > 0) {
      this.selectedFile = event.target['files'][0];
      this.selectedFileTypeInvalid = this.isFileTypeInvalid();
      this.selectedFileSizeInvalid = this.isFileSizeInvalid();
    } else {
      this.selectedFile = null;
      this.selectedFileTypeInvalid = false;
      this.selectedFileSizeInvalid = false;
    }
  }

  onTitleClicked() {
    if (this.isAllowedToEditTitle) {
      this.titleClicked.emit();
    }
  }

  private isFileSizeInvalid(): boolean {
    return this.selectedFile.size > this.maxFileSize;
  }

  private isFileTypeInvalid(): boolean {
    return !this.acceptedFileTypes.includes(this.selectedFile.type);
  }

  onDownloadClicked() {
    this.fileService.downloadFileByMeta(this.usedFileMeta);
  }
}
